const _tfImagePromises = {};
const _tfImageResolved = {};
const TF_PLACEHOLDER_SVG = (() => {
  const svgEl = document.querySelector('#shopify-product-placeholder svg');
  if (!svgEl) return '';
  const clone = svgEl.cloneNode(true);
  clone.setAttribute('style', 'position:absolute;inset:0;width:100%;height:100%');
  return clone.outerHTML;
})();
async function prefetchTorqueImages(hits) {
  const handles = [...new Set((hits || []).map((h) => h.handle).filter(Boolean))];
  await Promise.all(handles.map(async (handle) => {
    if (handle in _tfImageResolved) return;
    if (!_tfImagePromises[handle]) {
      _tfImagePromises[handle] = fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    const product = await _tfImagePromises[handle];
    _tfImageResolved[handle] = product?.featured_image || null;
  }));
}

const TF_CONFIG = {
  appId: 'OYVMFXBYSW',
  searchKey: 'd7ef4a519ec1754c716a550087c5d607',
  indexName: 'shopify_products',
  hitsPerPage: 25,
};

const TF_UNIT_MULTIPLIERS = {
  'Nm':     1,
  'cNm':    0.01,
  'Ncm':    0.01,
  'ft-lbs': 1.3558179,
  'in-lbs': 0.1129848,
};

const TF_TOLERANCE_LABELS = {
  'any':   '',
  '= 1':   'with a tolerance of 1%',
  '<= 2':  'with a tolerance of 2% or better',
  '= 2':   'with a tolerance of 2%',
  '<= 3':  'with a tolerance of 3% or better',
  '= 3':   'with a tolerance of 3%',
  '<= 4':  'with a tolerance of 4% or better',
  '= 4':   'with a tolerance of 4%',
  '<= 5':  'with a tolerance of 5% or better',
  '= 5':   'with a tolerance of 5%',
  '<= 6':  'with a tolerance of 6% or better',
  '= 6':   'with a tolerance of 6%',
  '<= 10': 'with a tolerance of 10% or better',
  '= 10':  'with a tolerance of 10%',
  '<= 15': 'with a tolerance of 15% or better',
  '= 15':  'with a tolerance of 15%',
};

// Algolia metafield paths used in filter/facet queries (meta.custom.tf_*)
const TF_FIELDS = {
  torqueMin:  'meta.custom.tf_torque_min_nm',
  torqueMax:  'meta.custom.tf_torque_max_nm',
  toolType:   'meta.custom.tf_tool_type',
  torqueFixed:'meta.custom.tf_torque_fixed',
  tolerance:  'meta.custom.tf_tolerance',
};

// Algolia stores tf_features as JSON-stringified arrays that get comma-split during
// indexing, producing broken facet keys. Each entry covers all variants for that feature.
const TF_FEATURE_FACET_MAP = {
  'Bi-Directional': [
    'meta.custom.tf_features:["Bi-Directional"]',
    'meta.custom.tf_features:["Bi-Directional',
  ],
  'Springless': [
    'meta.custom.tf_features:Springless"]',
    'meta.custom.tf_features:["Springless"]',
    'meta.custom.tf_features:Springless',
  ],
  'Ratcheting': [
    'meta.custom.tf_features:Ratcheting',
    'meta.custom.tf_features:["Ratcheting"]',
    'meta.custom.tf_features:Ratcheting"]',
  ],
  'Dual Scale': [
    'meta.custom.tf_features:Dual Scale',
    'meta.custom.tf_features:["Dual Scale"]',
    'meta.custom.tf_features:["Dual Scale',
    'meta.custom.tf_features:Dual Scale"]',
  ],
};

function joinList(arr) {
  if (arr.length <= 1) return arr[0] || '';
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return arr.slice(0, -1).join(', ') + ', and ' + arr[arr.length - 1];
}

class TorqueFinder {
  constructor() {
    this.state = {
      minTorque:     20,
      maxTorque:     50,
      unit:          'Nm',
      toolTypes:     [],
      adjustability: null,
      features:      [],
      tolerance:     'any',
      sort:          '',
      page:          0,
      vendors:       [],
      hexDrives:     [],
      squareDrives:  [],
      socketSizes:   [],
    };

    this.slider = null;

    this.els = {
      slider:      document.getElementById('tf-slider'),
      inputMin:    document.getElementById('tf-input-min'),
      inputMax:    document.getElementById('tf-input-max'),
      units:       document.getElementById('tf-units'),
      tolerance:   document.getElementById('tf-tolerance'),
      summary:     document.getElementById('tf-summary'),
      hits:        document.getElementById('tf-hits'),
      pagination:  document.getElementById('tf-pagination'),
      resultCount: document.getElementById('tf-result-count'),
      sort:        document.getElementById('tf-sort'),
    };
  }

  // --- Filter building ---

  get minNm() {
    return this.state.minTorque * TF_UNIT_MULTIPLIERS[this.state.unit];
  }

  get maxNm() {
    return this.state.maxTorque * TF_UNIT_MULTIPLIERS[this.state.unit];
  }

  buildFacetFilters() {
    const groups = [];
    // Adjustability — data-value="true" means fixed, "false" means adjustable
    if (this.state.adjustability === 'true')  groups.push([`${TF_FIELDS.torqueFixed}:true`]);
    if (this.state.adjustability === 'false') groups.push([`${TF_FIELDS.torqueFixed}:false`]);
    // Tool type — multi-select OR group
    if (this.state.toolTypes.length) groups.push(this.state.toolTypes.map((v) => `${TF_FIELDS.toolType}:${v}`));
    // Features — each selected feature is its own AND group (product must have all selected).
    // Use TF_FEATURE_FACET_MAP to cover all broken storage variants in Algolia.
    for (const feature of this.state.features) {
      const variants = TF_FEATURE_FACET_MAP[feature] || [`meta.custom.tf_features:${feature}`];
      groups.push(variants);
    }
    // Multi-selects
    if (this.state.vendors.length)      groups.push(this.state.vendors.map((v) => `vendor:${v}`));
    if (this.state.hexDrives.length)    groups.push(this.state.hexDrives.map((v) => `meta.custom.tf_hex_drive:${v}`));
    if (this.state.squareDrives.length) groups.push(this.state.squareDrives.map((v) => `meta.custom.tf_square_drive:${v}`));
    if (this.state.socketSizes.length)  groups.push(this.state.socketSizes.map((v) => `meta.custom.tf_socket_size:${v}`));
    return groups;
  }

  buildFilters() {
    const parts = [];

    // Qualifier: products must have a numeric torque_max — works without Algolia facet config
    parts.push(`${TF_FIELDS.torqueMax} >= 0.001`);

    // Torque range — only apply each bound when the user has entered a non-zero value
    const minNm = this.minNm;
    const maxNm = this.maxNm;
    if (minNm > 0) {
      parts.push(`${TF_FIELDS.torqueMin} <= ${minNm}`);
    }
    if (maxNm > 0) {
      parts.push(`${TF_FIELDS.torqueMax} >= ${maxNm}`);
    }

    // Tolerance — tf_tolerance is numeric; state value is an operator expression e.g. "= 1" or "<= 5"
    if (this.state.tolerance !== 'any') {
      parts.push(`${TF_FIELDS.tolerance} ${this.state.tolerance}`);
    }

    return parts.join(' AND ');
  }

  buildSummaryText(total = null) {
    const { minTorque, maxTorque, unit, toolTypes, adjustability, tolerance, features, vendors, hexDrives, squareDrives, socketSizes } = this.state;

    // Adjectives: fixed/adjustable + feature flags
    const adjectives = [
      adjustability === 'true'  ? 'fixed' : '',
      adjustability === 'false' ? 'adjustable' : '',
      ...features.map((f) => f.toLowerCase()),
    ].filter(Boolean).join(', ');

    // Tool word: lowercase type name, pluralised when total != 1
    const plural = total !== 1;
    let toolWord;
    if (toolTypes.length === 1) {
      const name = toolTypes[0].toLowerCase();
      toolWord = plural ? (/(?:ch|sh|x|s|z)$/i.test(name) ? name + 'es' : name + 's') : name;
    } else {
      toolWord = plural ? 'tools' : 'tool';
    }

    // Torque range
    const torquePart = minTorque === maxTorque
      ? `capable of ${minTorque} ${unit}`
      : `capable of both ${minTorque} and ${maxTorque} ${unit}`;

    // Brands
    const brandPart = vendors.length ? `from ${joinList(vendors)}` : '';

    // Head sizes — label each with its type
    const allSizes = [
      ...hexDrives.map((v)    => `${v} hex drive`),
      ...squareDrives.map((v) => `${v} square drive`),
      ...socketSizes.map((v)  => `${v} socket size`),
    ];
    const sizePart = allSizes.length ? `${joinList(allSizes)}` : '';

    // Tolerance
    const tolerancePart = TF_TOLERANCE_LABELS[tolerance] || '';

    return [
      `Showing all${adjectives ? ' ' + adjectives : ''}${sizePart ? ' ' + sizePart : ''} torque ${toolWord}`,
      brandPart,
      torquePart,
      tolerancePart,
    ].filter(Boolean).join(' ');
  }

  // --- Algolia ---

  async search(page = 0) {
    this.state.page = page;
    this.els.hits.innerHTML = `<div class="col-span-full flex items-center justify-center py-16">
      <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;
    this.els.pagination.innerHTML = '';
    if (this.els.resultCount) this.els.resultCount.textContent = '';
    this.els.summary.textContent = this.buildSummaryText();

    const filters = this.buildFilters();
    const indexName = this.state.sort
      ? `${TF_CONFIG.indexName}_${this.state.sort}`
      : TF_CONFIG.indexName;

    try {
      const facetFilters = this.buildFacetFilters();
      const body = {
        query: '',
        filters,
        facetFilters: facetFilters.length ? facetFilters : undefined,
        facets: [
          'vendor',
          'meta.custom.tf_tool_type',
          'meta.custom.tf_torque_fixed',
          'meta.custom.tf_features',
          'meta.custom.tf_hex_drive',
          'meta.custom.tf_square_drive',
          'meta.custom.tf_socket_size',
          'meta.custom.tf_tolerance',
        ],
        hitsPerPage: TF_CONFIG.hitsPerPage,
        page,
        attributesToRetrieve: [
          'title', 'handle', 'image', 'product_image', 'images', 'price',
          'compare_at_price', 'vendor', 'sku', 'id', 'objectID',
          'variants_count', 'inventory_quantity', 'inventory_available',
          'meta', 'tags',
        ],
      };

      const response = await fetch(
        `https://${TF_CONFIG.appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(indexName)}/query`,
        {
          method: 'POST',
          headers: {
            'X-Algolia-Application-Id': TF_CONFIG.appId,
            'X-Algolia-API-Key':        TF_CONFIG.searchKey,
            'Content-Type':             'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      // Deduplicate by product id (one record per variant in Algolia)
      const seen = new Set();
      const hits = (data.hits || []).filter((hit) => {
        if (seen.has(hit.id)) return false;
        seen.add(hit.id);
        return true;
      });

      const total = data.nbHits || 0;
      if (this.els.resultCount) {
        this.els.resultCount.textContent = `${total.toLocaleString()} result${total !== 1 ? 's' : ''}`;
      }
      this.updateFacetLists(data.facets || {});
      this.els.summary.textContent = this.buildSummaryText(total);
      await prefetchTorqueImages(hits);
      this.renderHits(hits, total);
      this.renderPagination(page, data.nbPages || 1);
    } catch (err) {
      console.error('[TF] Torque Finder search error:', err);
      this.els.hits.innerHTML = '<div class="tf-no-results">Search error. Please try again.</div>';
    }
  }

  // --- Rendering ---

  renderHits(hits, totalHits) {
    if (!hits.length) {
      this.els.hits.innerHTML = `
        <div class="tf-no-results">
          <h4>No tools found matching your criteria</h4>
          <p class="text-subtext">Try adjusting the torque range or removing some filters.</p>
        </div>`;
      return;
    }

    const cardsHTML = hits.map((hit) => `<div class="f-column card">${this.buildProductCard(hit)}</div>`).join('');
    this.els.hits.innerHTML = `
      <div class="f-grid f-grid--gap-medium f-grid--row-gap-large"
           style="--f-columns-mobile: 2; --f-columns-md: 3; --f-columns-xl: 5;">
        ${cardsHTML}
      </div>`;
  }

  buildProductCard(hit) {
    const url           = `/products/${hit.handle}`;
    const title         = hit.title || '';
    const rawTitle      = title.replace(/"/g, '&quot;');
    const image         = (hit.handle in _tfImageResolved)
      ? (_tfImageResolved[hit.handle] || '')
      : (hit.image || hit.product_image || '');
    const price         = hit.price != null ? hit.price : null;
    const compareAt     = hit.compare_at_price > hit.price ? hit.compare_at_price : null;
    const vendor        = hit.vendor || '';
    const sku           = hit.sku || '';
    const variantId     = hit.objectID || '';
    const productId     = hit.id || '';
    const isMulti       = (hit.variants_count || 1) > 1;
    const inStock       = hit.inventory_quantity > 0;
    const hideQuickAdd  = Array.isArray(hit.tags) && hit.tags.includes('Hide Quick Add');

    const imageHTML = image
      ? `<img src="${image}" alt="${rawTitle}" loading="lazy" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;">`
      : TF_PLACEHOLDER_SVG;

    const priceHTML = price != null
      ? `<div class="f-price${compareAt ? ' f-price--on-sale' : ''}">
           <div class="f-price__regular">
             <span class="f-price-item f-price-item--regular">$${Number(price).toFixed(2)}</span>
           </div>
           <div class="f-price__sale">
             <span class="f-price-item f-price-item--sale">$${Number(price).toFixed(2)}</span>
             <span class="f-price-item f-price-item--regular"><s>${compareAt ? `$${Number(compareAt).toFixed(2)}` : ''}</s></span>
           </div>
         </div>`
      : '';

    const inventoryHTML = hit.inventory_available !== undefined
      ? inStock
        ? `<p class="product__inventory product__inventory--in-stock font-body-bolder"><span class="product__inventory-icon"></span><span class="product__inventory-text">In Stock</span></p>`
        : `<p class="product__inventory font-body-bolder"><span class="oos">Out of stock</span></p>`
      : '';

    const judgemeHTML = hit.meta?.judgeme?.badge
      ? `<div class="jdgm-widget jdgm-preview-badge" data-id="${productId}" data-auto-install="false">${hit.meta.judgeme.badge}</div>`
      : '';

    const formId    = `quick-add-tf${productId}`;
    const listFormId = `list-view-quick-add-tf${productId}`;

    const cartIconSVG = `<svg viewBox="0 0 20 20" fill="none" class="icon icon-shopping-bag icon--medium icon--thick" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.875 18.125C7.56536 18.125 8.125 17.5654 8.125 16.875C8.125 16.1846 7.56536 15.625 6.875 15.625C6.18464 15.625 5.625 16.1846 5.625 16.875C5.625 17.5654 6.18464 18.125 6.875 18.125Z" fill="currentColor"/>
      <path d="M15 18.125C15.6904 18.125 16.25 17.5654 16.25 16.875C16.25 16.1846 15.6904 15.625 15 15.625C14.3096 15.625 13.75 16.1846 13.75 16.875C13.75 17.5654 14.3096 18.125 15 18.125Z" fill="currentColor"/>
      <path d="M1.25 2.5H3.125L5.99609 12.8344C6.06916 13.0976 6.22643 13.3296 6.44384 13.4949C6.66126 13.6603 6.92685 13.7499 7.2 13.75H14.9219C15.1952 13.7501 15.4609 13.6605 15.6785 13.4952C15.8961 13.3298 16.0535 13.0977 16.1266 12.8344L18.125 5.625H3.99297" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

    const spinner = `<div class="loading__spinner hidden">
      <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;

    const atcHTML = hideQuickAdd ? '' : isMulti
      ? `<div class="product-card__actions product-card__main-actions no-js-hidden wrap-product-info">
           <a href="${url}" class="product-card__atc product-card__action-button btn btn--primary">
             <div class="btn__text flex gap-1 items-center">
               <span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">${cartIconSVG}</span>
               <span class="product-card__atc-text product-card__action-text">Options</span>
             </div>
           </a>
         </div>`
      : `<form method="post" action="/cart/add" id="${formId}" class="product-card__actions product-card__main-actions" novalidate data-type="add-to-cart-form" is="product-form">
           <input type="hidden" name="id" value="${variantId}" class="product-variant-id">
           <button id="${formId}-submit" type="submit" name="add" aria-label="Add to cart" class="product-card__atc product-card__action-button btn btn--primary">
             <div class="btn__text flex gap-1 items-center">
               <span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">${cartIconSVG}</span>
               <span class="product-card__atc-text product-card__action-text">Add</span>
             </div>
             ${spinner}
           </button>
         </form>`;

    const listAtcHTML = hideQuickAdd ? '' : isMulti
      ? `<div class="product-card__list-actions-main">
           <a href="${url}" class="product-card__list-atc btn btn--primary">
             <span class="btn__text flex gap-1 items-center">Options</span>
           </a>
         </div>`
      : `<form method="post" action="/cart/add" id="${listFormId}" class="product-card__list-actions-main" novalidate data-type="add-to-cart-form" is="product-form">
           <input type="hidden" name="id" value="${variantId}" class="product-variant-id">
           <button id="${listFormId}-submit" type="submit" name="add" aria-label="Add to cart" class="product-card__list-atc btn btn--primary w-full">
             <span class="btn__text flex gap-1 items-center">Add</span>
             ${spinner}
           </button>
         </form>`;

    return `
      <div class="product-card product-card-style-standard color-inherit" data-algolia-object-id="${variantId}" data-handle="${hit.handle}">
        <div class="product-card__wrapper h-full">
          <div class="product-card__image-wrapper product-card__image-wrapper--main-only color-scheme-1 bg-none">
            <a href="${url}" aria-label="${rawTitle}" tabindex="-1">
              <div class="block">
                <div class="media-wrapper product-card__image product-card__image--main loaded"
                     style="position:relative;overflow:hidden;padding-bottom:100%;background:#f0f0f0;">
                  ${imageHTML}
                </div>
              </div>
            </a>
            ${atcHTML}
          </div>
          <div class="product-card__info text-left">
            <h3 class="product-card__title text-pcard-title">
              <a class="reversed-link block" href="${url}"><span class="reversed-link__text">${title}</span></a>
            </h3>
            ${sku ? `<div class="product-card__sku">SKU: ${sku}</div>` : ''}
            ${priceHTML}
            ${judgemeHTML}
            ${inventoryHTML}
          </div>
          <div class="product-card__list-actions hidden gap-3 flex-col">
            ${listAtcHTML}
            <a href="${url}" class="btn btn--secondary">
              <span class="btn__text flex gap-1 items-center">View details</span>
            </a>
          </div>
        </div>
      </div>
    `;
  }

  renderPagination(currentPage, totalPages) {
    this.els.pagination.innerHTML = '';
    if (totalPages <= 1) return;

    const CARET_LEFT  = '<svg class="icon icon-caret-left rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 16.25L6.25 10L12.5 3.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const CARET_RIGHT = '<svg class="icon icon-caret-right rtl-flip-x icon--extra-small icon--thick" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const goTo = (p) => { this.search(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const parseSVG = (str) => { const d = document.createElement('div'); d.innerHTML = str; return d.firstChild; };
    const wrapLi = (el) => { const li = document.createElement('li'); li.appendChild(el); return li; };

    const makePageLink = (p) => {
      const a = document.createElement('a');
      a.className = 'pagination__item';
      a.setAttribute('aria-label', `Page ${p + 1}`);
      a.href = '#';
      a.textContent = p + 1;
      a.addEventListener('click', (e) => { e.preventDefault(); goTo(p); });
      return a;
    };

    const makeEllipsis = () => {
      const span = document.createElement('span');
      span.className = 'pagination__item';
      span.textContent = '…';
      return span;
    };

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination');

    const ul = document.createElement('ul');
    ul.className = 'pagination__list list-unstyled flex items-center flex-wrap justify-center';
    ul.setAttribute('role', 'list');

    // Previous
    if (currentPage > 0) {
      const a = document.createElement('a');
      a.className = 'pagination__item pagination__item--previous pagination__item-arrow motion-reduce';
      a.href = '#';
      a.appendChild(parseSVG(CARET_LEFT));
      const s = document.createElement('span'); s.textContent = 'Prev'; a.appendChild(s);
      a.addEventListener('click', (e) => { e.preventDefault(); goTo(currentPage - 1); });
      ul.appendChild(wrapLi(a));
    }

    // Page window
    const delta = 2;
    const pages = [];
    for (let p = 0; p < totalPages; p++) {
      if (p === 0 || p === totalPages - 1 || (p >= currentPage - delta && p <= currentPage + delta)) {
        pages.push(p);
      }
    }

    let prev = null;
    for (const p of pages) {
      if (prev !== null && p - prev > 1) ul.appendChild(wrapLi(makeEllipsis()));
      if (p === currentPage) {
        const a = document.createElement('a');
        a.className = 'pagination__item pagination__item--current background-2';
        a.setAttribute('role', 'link');
        a.setAttribute('aria-disabled', 'true');
        a.setAttribute('aria-current', 'page');
        a.setAttribute('aria-label', `Page ${p + 1}`);
        a.textContent = p + 1;
        ul.appendChild(wrapLi(a));
      } else {
        ul.appendChild(wrapLi(makePageLink(p)));
      }
      prev = p;
    }

    // Next
    if (currentPage < totalPages - 1) {
      const a = document.createElement('a');
      a.className = 'pagination__item pagination__item--next pagination__item-arrow motion-reduce';
      a.href = '#';
      const ns = document.createElement('span'); ns.textContent = 'Next'; a.appendChild(ns);
      a.appendChild(parseSVG(CARET_RIGHT));
      a.addEventListener('click', (e) => { e.preventDefault(); goTo(currentPage + 1); });
      ul.appendChild(wrapLi(a));
    }

    nav.appendChild(ul);
    this.els.pagination.appendChild(nav);
  }

  // --- Slider ---

  initSlider() {
    this.slider = noUiSlider.create(this.els.slider, {
      start:   [20, 50],
      connect: false,
      range: {
        'min':  0,
        '2%':   0.01,
        '10%':  0.1,
        '18%':  1,
        '32%':  10,
        '50%':  50,
        '68%':  100,
        '82%':  500,
        '90%':  1000,
        '98%':  4000,
        'max':  6000,
      },
      pips: {
        mode:    'values',
        values:  [
          0.01, 0.025, 0.05, 0.075,
          0.1, 0.25, 0.5, 0.75,
          1, 2, 3, 5, 7,
          10, 20, 30,
          50, 75,
          100, 200, 300, 400,
          500, 750,
          1000, 1500, 2000, 3000,
          4000,
        ],
        density: 2,
        filter:  (value) => {
          if (value > 4000) return -1;
          const labeled = [0.01, 0.1, 1, 10, 50, 100, 500, 1000, 4000];
          // 1 = PipsType.LargeValue → .noUi-marker-large (tall labeled tick)
          // 0 = PipsType.NoValue   → .noUi-marker-normal (short unlabeled tick)
          if (labeled.some((v) => Math.abs(v - value) < 0.0001)) return 1;
          return 0;
        },
        format:  {
          to: (v) => v > 10 ? Math.round(v) : parseFloat(v.toFixed(2)),
          from: (v) => parseFloat(v),
        },
      },
      tooltips: [
        { to: (v) => parseFloat(v.toFixed(2)), from: (v) => parseFloat(v) },
        { to: (v) => parseFloat(v.toFixed(2)), from: (v) => parseFloat(v) },
      ],
    });

    // Custom connect bar — bypasses noUiSlider's connect CSS entirely
    const tfBar = document.createElement('div');
    tfBar.className = 'tf-connect-bar';
    tfBar.style.cssText = 'position:absolute;height:10px;background:#dc3e3b;border-radius:5px;top:1%;transform:translateY(-50%);z-index:1;pointer-events:none;left:0;width:0;';
    this.els.slider.querySelector('.noUi-base').appendChild(tfBar);
    this._tfBar = tfBar;

    // Force handle vertical position via JS — CDN .noUi-horizontal .noUi-handle specificity beats CSS-only overrides
    this.els.slider.querySelectorAll('.noUi-handle').forEach((el) => {
      el.style.setProperty('top', '50%', 'important');
      el.style.setProperty('transform', 'translateY(-50%)', 'important');
    });

    // Show minor ticks that CDN hides by default
    this.els.slider.querySelectorAll('.noUi-marker-normal, .noUi-marker-sub').forEach((el) => {
      el.style.setProperty('display', 'block', 'important');
      el.style.setProperty('top', '25px', 'important');
      el.style.setProperty('height', '16px', 'important');
    });

    // Sync slider → inputs (live, while dragging) and update custom bar
    this.slider.on('update', (values, handle, unencoded, tap, positions) => {
      this.els.inputMin.value = parseFloat(parseFloat(values[0]).toFixed(4));
      this.els.inputMax.value = parseFloat(parseFloat(values[1]).toFixed(4));
      if (this._tfBar) {
        this._tfBar.style.left  = positions[0] + '%';
        this._tfBar.style.width = (positions[1] - positions[0]) + '%';
      }
    });

    // Fire search only on release
    this.slider.on('change', ([min, max]) => {
      this.state.minTorque = parseFloat(min);
      this.state.maxTorque = parseFloat(max);
      this.search(0);
    });
  }

  // --- Event wiring ---

  initInputs() {
    let debounceTimer = null;

    const syncAndSearch = () => {
      const active = document.activeElement;
      let min = parseFloat(this.els.inputMin.value);
      let max = parseFloat(this.els.inputMax.value);
      if (isNaN(min) || isNaN(max)) return;

      const swapped = min > max;
      if (swapped) { [min, max] = [max, min]; }

      // Only write back to a field if it isn't the one the user is actively typing in,
      // or if a swap happened (preserves decimals-in-progress like "1.")
      if (swapped || active !== this.els.inputMin) this.els.inputMin.value = min;
      if (swapped || active !== this.els.inputMax) this.els.inputMax.value = max;

      // Move focus to whichever field now holds the value the user was editing
      if (swapped) {
        if (active === this.els.inputMin) this.els.inputMax.focus();
        else if (active === this.els.inputMax) this.els.inputMin.focus();
      }

      this.state.minTorque = min;
      this.state.maxTorque = max;

      const [sMin, sMax] = this.slider.get();
      if (parseFloat(sMin) !== min || parseFloat(sMax) !== max) {
        this.slider.set([min, max]);
      }
      this.search(0);
    };

    const syncDebounced = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(syncAndSearch, 150);
    };

    this.els.inputMin.addEventListener('input', syncDebounced);
    this.els.inputMax.addEventListener('input', syncDebounced);
    this.els.inputMin.addEventListener('change', syncAndSearch);
    this.els.inputMax.addEventListener('change', syncAndSearch);

    this.els.units.addEventListener('change', () => {
      this.state.unit = this.els.units.value;
      this.search(0);
    });

    this.els.tolerance.addEventListener('change', () => {
      this.state.tolerance = this.els.tolerance.value;
      this.search(0);
    });
  }

  updateFacetLists(facets) {
    const FACET_CONFIGS = [
      { id: 'tf-facet-vendor',       key: 'vendor',                      stateKey: 'vendors' },
      { id: 'tf-facet-tool-type',    key: 'meta.custom.tf_tool_type',    stateKey: 'toolTypes' },
      { id: 'tf-facet-hex-drive',    key: 'meta.custom.tf_hex_drive',    stateKey: 'hexDrives',    suffix: ' Hex Drive' },
      { id: 'tf-facet-square-drive', key: 'meta.custom.tf_square_drive', stateKey: 'squareDrives', suffix: ' Square Drive' },
      { id: 'tf-facet-socket-size',  key: 'meta.custom.tf_socket_size',  stateKey: 'socketSizes',  suffix: ' Socket Size' },
    ];

    for (const { id, key, stateKey, suffix = '' } of FACET_CONFIGS) {
      const container = document.getElementById(id);
      if (!container) continue;

      const values = facets[key];
      if (!values || Object.keys(values).length === 0) {
        container.innerHTML = '<p class="tf-facet-empty"></p>';
        continue;
      }

      const selected = this.state[stateKey];
      const sorted = Object.entries(values).sort(([a], [b]) => {
        const na = parseFloat(a), nb = parseFloat(b);
        if (!isNaN(na) && !isNaN(nb)) return na - nb;
        return a.localeCompare(b);
      });

      container.innerHTML = sorted.map(([value, count]) => {
        const escaped = value.replace(/"/g, '&quot;');
        const checked = selected.includes(value) ? ' checked' : '';
        return `<label class="tf-facet-label"><input type="checkbox" class="tf-check tf-check--multi" data-filter="${stateKey}" data-value="${escaped}"${checked}><span>${value}${suffix}</span><span class="tf-facet-count">${count}</span></label>`;
      }).join('');
    }
  }

  initButtons() {
    const container = document.getElementById('FacetFiltersContainer');
    if (!container) return;

    container.addEventListener('change', (e) => {
      const checkbox = e.target.closest('.tf-check[data-filter]');
      if (!checkbox) return;

      const filter = checkbox.dataset.filter;
      const value  = checkbox.dataset.value;

      if (checkbox.classList.contains('tf-check--toggle')) {
        // Independent feature toggle (dualScale, biDirectional, etc.)
        this.state[filter] = checkbox.checked;
      } else if (checkbox.classList.contains('tf-check--multi')) {
        // True multi-select (vendor, hexDrives, squareDrives, socketSizes)
        const arr = this.state[filter];
        if (checkbox.checked) {
          if (!arr.includes(value)) arr.push(value);
        } else {
          const idx = arr.indexOf(value);
          if (idx > -1) arr.splice(idx, 1);
        }
      } else {
        // Radio-style: one active at a time (adjustability, toolType)
        if (checkbox.checked) {
          container.querySelectorAll(`.tf-check[data-filter="${filter}"]`).forEach((s) => {
            if (s !== checkbox) s.checked = false;
          });
          this.state[filter] = value;
        } else {
          this.state[filter] = null;
        }
      }
      this.search(0);
    });
  }

  initSort() {
    if (!this.els.sort) return;
    this.els.sort.addEventListener('change', () => {
      this.state.sort = this.els.sort.value;
      this.search(0);
    });
  }

  initFilterToggle() {
    const btn      = document.getElementById('tf-filter-toggle');
    const panel    = document.getElementById('FacetFiltersContainer');
    const closeBtn = document.getElementById('tf-filter-close');
    if (!btn || !panel) return;

    const openPanel = () => {
      panel.classList.add('tf-open');
      btn.setAttribute('aria-expanded', 'true');
      document.body.classList.add('tf-filter-open');
    };

    const closePanel = () => {
      panel.classList.remove('tf-open');
      btn.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('tf-filter-open');
    };

    btn.addEventListener('click', () => {
      panel.classList.contains('tf-open') ? closePanel() : openPanel();
    });

    if (closeBtn) closeBtn.addEventListener('click', closePanel);
  }

  // --- Init ---

  init() {
    this.initSlider();
    this.initInputs();
    this.initButtons();
    this.initSort();
    this.initFilterToggle();
    this.search(0);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('torque-finder')) {
    new TorqueFinder().init();
  }
});
